import React, { useState } from 'react';
import { Modal, Box, Button } from '@mui/material';

import { Loading, ErrorMessage, StatusMessage } from '@/src/ui';
import type { SchoolBaseDTO } from '../models/schoolDTOs';

interface DeleteSchoolModalProps {
    open: boolean;
    school?: SchoolBaseDTO | null;
    onDelete: (schoolId: number) => Promise<void> | void;
    onClose: (_shouldRefresh: boolean, _isInTimeout: boolean) => void;
    error?: string;
}

const DeleteSchoolModal: React.FC<DeleteSchoolModalProps> = ({
    open,
    school,
    onDelete,
    onClose,
    error,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [firstConfirm, setFirstConfirm] = useState(false);
    const [localError, setLocalError] = useState('');

    const handleSubmit = async (e: React.FormEvent<any>) => {
        e.preventDefault();
        if (submitting || success) return;
        if (!school?.id) {
            setLocalError('Unable to load school Id');
            return;
        }
        if (!firstConfirm) {
            setFirstConfirm(true);
            return;
        }
        setSubmitting(true);
        setLocalError('');
        try {
            await onDelete(school.id);
            setSuccess(true);
            setTimeout(() => {
                onClose(true, false);
                setFirstConfirm(false);
                setSuccess(false);
            }, 750);
        } catch {
            setLocalError('Failed to delete school');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={() => onClose(false, success)}>
            <Box sx={{ p: 4, bgcolor: 'background.paper', margin: 'auto', mt: 8, width: 400 }}>
                <h2>Delete School</h2>
                <p>Are you sure you want to delete this school?</p>
                <p>
                    <strong>Name:</strong> {school?.name}
                </p>
                <p>
                    <strong>Code:</strong> {school?.code}
                </p>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    {!firstConfirm ? (
                        <>
                            <Button variant="outlined" onClick={() => onClose(false, false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="error" onClick={handleSubmit}>
                                Delete
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                disabled={submitting || success}
                                variant="contained"
                                color="error"
                                onClick={handleSubmit}
                            >
                                Confirm Delete
                            </Button>
                            <Button
                                disabled={submitting || success}
                                variant="outlined"
                                onClick={() => onClose(false, false)}
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                </Box>
                <ErrorMessage error={localError || error} />
                <StatusMessage
                    visible={success}
                    status="School deleted successfully!"
                    style={{ marginTop: 8 }}
                />
                <Loading visible={submitting} message="Processing..." size="sm" />
            </Box>
        </Modal>
    );
};

export default DeleteSchoolModal;
